/**
 * Component: Chatbot
 * Main chatbot interface with RAG support
 */

import React, { useState, useEffect, useRef } from 'react';
import { useChatSession } from '../hooks/useChatSession.js';
import { sendMessage } from '../utils/chat-api-client.js';
import ChatConversation from '../components/ChatConversation.jsx';
import ChatInput from '../components/ChatInput.jsx';
import RagSourceViewer from '../components/RagSourceViewer.jsx';
import { LoadingOverlay } from '../components/index.js';
import { useTranslation } from '../context/TranslationContext';
import debug from '@commons-shared/debug.js';
import { config } from '../config/environment.js';

export default function ChatbotPage() {
  const { t, loadPageTranslations } = useTranslation();
  const [worldId, setWorldId] = useState(null);

  // Load page translations on mount
  React.useEffect(() => {
    loadPageTranslations('CHATBOT');
  }, [loadPageTranslations]);

  // Récupérer l'ID du monde sélectionné depuis le localStorage
  useEffect(() => {
    try {
      const waWorldString = localStorage.getItem('wa_world');
      if (waWorldString) {
        const waWorld = JSON.parse(waWorldString);
        setWorldId(waWorld.id || null);
      } else {
        setWorldId(null);
      }
    } catch (error) {
      debug.error(config, 'ChatbotPage: Erreur lors de la lecture de wa_world:', error);
      setWorldId(null);
    }
  }, []);

  const {
    sessionId,
    messages,
    loading: sessionLoading,
    error: sessionError,
    initializeSession,
    addLocalMessage,
    updateMessage,
    setError: setSessionError,
  } = useChatSession(worldId);

  const [isSending, setIsSending] = useState(false);
  const [selectedSources, setSelectedSources] = useState(null);
  const [useRag, setUseRag] = useState(true);
  const conversationEndRef = useRef(null);

  // Initialize session on mount
  useEffect(() => {
    if (worldId) {
      initializeSession();
    }
  }, [worldId, initializeSession]);

  // Auto-scroll to bottom
  useEffect(() => {
    conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (userMessage) => {
    if (!sessionId || !userMessage.trim()) return;

    setIsSending(true);
    setSessionError(null);

    try {
      // Add user message to UI
      addLocalMessage('user', userMessage);

      // Send to backend
      const result = await sendMessage(sessionId, userMessage, useRag);
      debug.log(config, '[ChatbotPage] sendMessage result:', {
        messageId: result.message.message_id,
        ragSourcesCount: result.ragSources?.length || 0,
        ragSources: result.ragSources,
      });

      // Add assistant response
      const assistantMsg = addLocalMessage('assistant', result.message.content);
      debug.log(config, '[ChatbotPage] Created local assistant message:', {
        localId: assistantMsg.message_id,
      });

      // Update message with server data and RAG sources in one call
      const updates = {
        message_id: result.message.message_id,
      };
      
      if (result.ragSources && result.ragSources.length > 0) {
        updates.rag_sources = result.ragSources;
        debug.log(config, '[ChatbotPage] Adding rag_sources to updates:', updates.rag_sources);
      }
      
      debug.log(config, '[ChatbotPage] Calling updateMessage with:', {
        localId: assistantMsg.message_id,
        updates,
      });
      
      updateMessage(assistantMsg.message_id, updates);
    } catch (error) {
      setSessionError(error.message || 'Error sending message');
      debug.error(config, 'ChatbotPage: Erreur lors de l\'envoi du message:', error);
    } finally {
      setIsSending(false);
    }
  };

  if (sessionLoading) {
    return <LoadingOverlay />;
  }

  if (!worldId) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{t('CHATBOT', 'NO_WORLD', 'Please select a world to start chatting')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <h1 className="text-2xl font-bold text-gray-800">{t('CHATBOT', 'TITLE', 'AI Chatbot')}</h1>
        <p className="text-sm text-gray-600 mt-1">{t('CHATBOT', 'DESCRIPTION', 'Chat with an AI assistant powered by your world knowledge base')}</p>
      </div>

      {/* Controls */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex gap-4 items-center">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={useRag}
            onChange={(e) => setUseRag(e.target.checked)}
            className="w-4 h-4"
          />
          <span className="text-sm text-gray-700">{t('CHATBOT', 'USE_RAG', 'Use world knowledge base (RAG)')}</span>
        </label>
      </div>

      {/* Conversation */}
      <div className="flex-1 overflow-y-auto p-6">
        <ChatConversation
          messages={messages}
          onSourceClick={setSelectedSources}
        />
        <div ref={conversationEndRef} />
      </div>

      {/* Sources Viewer */}
      {selectedSources && (
        <RagSourceViewer
          sources={selectedSources}
          onClose={() => setSelectedSources(null)}
        />
      )}

      {/* Error */}
      {sessionError && (
        <div className="bg-red-50 border-t border-red-200 px-6 py-3">
          <p className="text-sm text-red-800">{sessionError}</p>
        </div>
      )}

      {/* Input */}
      <ChatInput
        onSendMessage={handleSendMessage}
        disabled={isSending || !sessionId}
        isLoading={isSending}
      />
    </div>
  );
}
